import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('movemate_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiry and maintenance mode
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('movemate_token');
      localStorage.removeItem('movemate_user');
      window.location.href = '/login';
    }
    
    // Handle maintenance mode
    if (error.response?.status === 503 && error.response?.data?.is_maintenance) {
      // Store maintenance info and redirect
      localStorage.setItem('maintenance_message', error.response.data.message || 'System under maintenance');
      localStorage.setItem('maintenance_eta', error.response.data.eta_message || '');
      window.location.href = '/maintenance';
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  registerCompany: (data) => api.post('/auth/register-company', data),
  setup2FA: () => api.post('/auth/setup-2fa'),
  enable2FA: (code) => api.post('/auth/enable-2fa', null, { params: { verification_code: code } }),
  disable2FA: (password) => api.post('/auth/disable-2fa', null, { params: { password } }),
  loginCode: (data) => api.post('/auth/login-code', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  resendVerification: (data) => api.post('/auth/resend-verification', data),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
};

// Users API
export const usersAPI = {
  list: () => api.get('/users/list'),
  create: (data) => api.post('/users/create', data),
  regenerateCode: (userId) => api.post(`/users/${userId}/regenerate-code`),
};

// Jobs API
export const jobsAPI = {
  list: () => api.get('/jobs'),
  get: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
};

// Inventory API - Uses ENHANCED inventory (with QR, photos, packing features)
export const inventoryAPI = {
  list: (jobId) => api.get(`/jobs/${jobId}/inventory-enhanced`),
  getByJob: (jobId) => api.get(`/jobs/${jobId}/inventory-enhanced`),
  create: (jobId, data) => api.post(`/jobs/${jobId}/inventory-enhanced`, data),
  update: (id, data) => api.put(`/inventory-enhanced/${id}`, data),
  delete: (id) => api.delete(`/inventory-enhanced/${id}`),
  scanAI: (data) => api.post('/inventory/ai-scan', data),
};

// QR Code API
export const qrAPI = {
  generateBatch: (data) => api.post('/qr/batch', data),
  getBatches: () => api.get('/qr/batches'),
  getCode: (code) => api.get(`/qr/${code}`),
};

// Companies API
export const companiesAPI = {
  get: (id) => api.get(`/companies/${id}`),
  update: (id, data) => api.put(`/companies/${id}`, data),
};

// Company API (current company)
export const companyAPI = {
  getSubscription: () => api.get('/subscription'),
  getProfile: () => api.get('/company/profile'),
};

// Estimates API
export const estimatesAPI = {
  list: (jobId) => api.get(`/jobs/${jobId}/estimates`),
  getByJob: (jobId) => api.get(`/jobs/${jobId}/estimates`),
  get: (id) => api.get(`/estimates/${id}`),
  create: (data) => api.post('/estimates', data),
  update: (id, data) => api.put(`/estimates/${id}`, data),
};

export default api;
